import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Types
interface Notification {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
  autoHide?: boolean;
  duration?: number;
}

interface UiState {
  notifications: Notification[];
  sidebarOpen: boolean;
  darkMode: boolean;
}

// Initial state
const initialState: UiState = {
  notifications: [],
  sidebarOpen: false,
  darkMode: localStorage.getItem('darkMode') === 'true'
};

// Slice
const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    addNotification(state, action: PayloadAction<Omit<Notification, 'id'>>) {
      const id = Date.now().toString();
      state.notifications.push({
        id,
        ...action.payload,
        autoHide: action.payload.autoHide !== false, // Default to true
        duration: action.payload.duration || 5000 // Default to 5 seconds
      });
    },
    removeNotification(state, action: PayloadAction<string>) {
      state.notifications = state.notifications.filter(
        notification => notification.id !== action.payload
      );
    },
    clearNotifications(state) {
      state.notifications = [];
    },
    toggleSidebar(state) {
      state.sidebarOpen = !state.sidebarOpen;
    },
    setSidebarOpen(state, action: PayloadAction<boolean>) {
      state.sidebarOpen = action.payload;
    },
    toggleDarkMode(state) {
      state.darkMode = !state.darkMode;
      localStorage.setItem('darkMode', state.darkMode.toString());
    },
    setDarkMode(state, action: PayloadAction<boolean>) {
      state.darkMode = action.payload;
      localStorage.setItem('darkMode', action.payload.toString());
    }
  }
});

export const {
  addNotification,
  removeNotification,
  clearNotifications,
  toggleSidebar,
  setSidebarOpen,
  toggleDarkMode,
  setDarkMode
} = uiSlice.actions;

export default uiSlice.reducer;
